import { UserProfile, saveUserProfile } from './profile';

const LAST_SYNCED_KEY = 'weiai_guandan_last_synced_at';

export interface SyncResponse {
  success: boolean;
  profile?: UserProfile;
  syncedAt?: string;
  source?: string;
  accountEmail?: string;
  error?: string;
  message?: string;
}

/**
 * Sync local profile to the cloud with bidirectional smart merge.
 * If user is authenticated, saves to Auth0 user_metadata.
 * If user is guest, registers their 6-digit PIN in the cloud registry.
 */
export async function syncProfileToCloud(
  profile: UserProfile,
  token?: string,
  userId?: string
): Promise<SyncResponse> {
  try {
    const res = await fetch('/api/sync', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        action: 'sync',
        profile,
        userId,
        syncPin: profile.syncPin,
      }),
    });

    if (!res.ok) {
      const errData = await res.json().catch(() => ({}));
      return {
        success: false,
        error: errData.error || `HTTP ${res.status}: 同步服务暂时不可用`,
      };
    }

    const data: SyncResponse = await res.json();
    if (data.success && data.profile) {
      const syncedAt = data.syncedAt || new Date().toISOString();
      localStorage.setItem(LAST_SYNCED_KEY, syncedAt);
      saveUserProfile(data.profile);
    }
    return data;
  } catch (err: any) {
    console.warn('syncProfileToCloud error:', err);
    return {
      success: false,
      error: err.message || '网络连接异常，无法连接云端同步服务器',
    };
  }
}

/**
 * Load profile from cloud for an authenticated Auth0 user.
 */
export async function loadProfileFromCloud(
  token?: string,
  userId?: string
): Promise<SyncResponse> {
  try {
    const res = await fetch('/api/sync', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        action: 'load',
        userId,
      }),
    });

    if (!res.ok) {
      const errData = await res.json().catch(() => ({}));
      return {
        success: false,
        error: errData.error || `HTTP ${res.status}: 无法读取云端战绩`,
      };
    }

    const data: SyncResponse = await res.json();
    if (data.success && data.profile) {
      const syncedAt = data.syncedAt || new Date().toISOString();
      localStorage.setItem(LAST_SYNCED_KEY, syncedAt);
      saveUserProfile(data.profile);
    }
    return data;
  } catch (err: any) {
    console.warn('loadProfileFromCloud error:', err);
    return {
      success: false,
      error: err.message || '网络连接异常，无法读取云端档案',
    };
  }
}

/**
 * Import profile across devices using the 6-digit Sync PIN.
 */
export async function importProfileByPin(syncPin: string): Promise<SyncResponse> {
  try {
    const cleanPin = syncPin.trim();
    if (cleanPin.length !== 6) {
      return { success: false, error: '请输入正确的 6 位数字同步码' };
    }

    const res = await fetch('/api/sync', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'import_pin',
        syncPin: cleanPin,
      }),
    });

    const data: SyncResponse = await res.json().catch(() => ({
      success: false,
      error: `HTTP ${res.status}: 无法解析服务器返回`,
    }));

    if (data.success && data.profile) {
      const syncedAt = data.syncedAt || new Date().toISOString();
      localStorage.setItem(LAST_SYNCED_KEY, syncedAt);
      saveUserProfile(data.profile);
    }
    return data;
  } catch (err: any) {
    console.warn('importProfileByPin error:', err);
    return {
      success: false,
      error: err.message || '网络连接异常，无法校验同步码',
    };
  }
}

/**
 * Retrieve the last successfully synced ISO date string.
 */
export function getLastSyncedAt(): string | null {
  return localStorage.getItem(LAST_SYNCED_KEY);
}
